import { useState } from "react";
import Logo from "./logo";
import StudioPromptEmbed from "./studio-prompt-embed";
import ActionsFooter from "./actions-footer";
import { isSpotifyEnabled } from "../../sites";

const YOUTUBE_URL = "https://www.youtube.com/";
const YOUTUBE_MUSIC_URL = "https://music.youtube.com/";
const SOUNDCLOUD_URL = "https://soundcloud.com/";
const SPOTIFY_URL = "https://open.spotify.com/";
const BANDCAMP_URL = "https://bandcamp.com/";
const APPLE_MUSIC_URL = "https://music.apple.com/";

// The sites that run on a permission the listener grants, by id
// (sites/index.js), as the gate names them.
const SITE_NAMES = {
  soundcloud: "SoundCloud",
  spotify: "Spotify",
  bandcamp: "Bandcamp",
  "apple-music": "Apple Music",
};
// Spotify, Apple Music and Bandcamp are in beta, and say so wherever they
// are offered.
const BETA_SITES = new Set(["spotify", "apple-music", "bandcamp"]);

// "YouTube, SoundCloud and Bandcamp", with the betas the listener switched on.
export function siteList(names, word = "and") {
  if (names.length < 2) return names.join("");
  return `${names.slice(0, -1).join(", ")} ${word} ${names[names.length - 1]}`;
}

function formatMinutes(minutes) {
  const value = Math.max(0, Math.floor(Number(minutes) || 0));
  return value >= 60
    ? `${Math.floor(value / 60)}h ${value % 60}m`
    : `${value} min`;
}

// What the plan card says, from the same props the header reads. Slowing down
// is free at any meter reading, so the free lines never imply it stops.
export function describePlan({
  isPro,
  trialMode,
  trialEnded,
  remainingTrialDays,
  usageHit,
  usageRemaining,
  usagePeriod,
}) {
  if (isPro) {
    return { title: "Slowed Pro", detail: "Every effect, mood and skin." };
  }
  if (trialMode && remainingTrialDays >= 0) {
    return {
      title: "Pro trial",
      detail:
        remainingTrialDays > 0
          ? `${remainingTrialDays} ${remainingTrialDays === 1 ? "day" : "days"} left`
          : "Last day",
    };
  }
  if (trialEnded === true) {
    const resets = usagePeriod === "monthly" ? "resets monthly" : "resets weekly";
    return {
      title: "Free plan",
      detail: usageHit
        ? `Pro minutes used up, ${resets}`
        : `${formatMinutes(usageRemaining)} of Pro left, ${resets}`,
    };
  }
  return { title: "Free plan", detail: "Slowing down is always free." };
}

// On SoundCloud or Spotify before Slowed has been allowed to run there.
// Chrome asks the listener itself; this is the click that lets it.
function SiteAccess({ site, busy, error, onEnableSiteClick }) {
  const name = SITE_NAMES[site] || site;
  const label = BETA_SITES.has(site) ? `${name} (Beta)` : name;
  return (
    <>
      <h1 className="unsupported-gate__title">Turn on Slowed for {label}</h1>
      <p className="unsupported-gate__text">
        Chrome will ask to let Slowed run on {name} and its player. The page
        reloads once, then click Slowed again.
      </p>
      {error && (
        <p className="unsupported-gate__text" role="alert">
          {error}
        </p>
      )}
      <div className="unsupported-gate__ctas">
        <button
          type="button"
          className="unsupported-gate__cta unsupported-gate__cta--primary"
          disabled={busy}
          aria-busy={busy}
          onClick={() => onEnableSiteClick?.(site)}
        >
          Turn on for {label}
        </button>
      </div>
    </>
  );
}

// Where the floating panel cannot mount, the native popup shows this instead
// of the controls: a way to a site Slowed runs on, the plan, and the links
// that don't need a player (feedback, help, billing).
export default function UnsupportedGate({
  isPro,
  trialMode,
  trialEnded,
  remainingTrialDays,
  usageHit,
  usageRemaining,
  usagePeriod,
  hideManagePlan,
  billingLoading,
  upgradeError,
  isFeedbackEnabled,
  isRateAppEnabled,
  toolUrl,
  // Add Song: Studio opens on its own file chooser and plays the song.
  onAddSongClick,
  // Slow It in Studio's prompt (./studio-prompt-embed.jsx): Studio's
  // address with the listener's words in it, to open in a tab.
  onPromptSlowIt,
  // The controls' own header (./popup-header.jsx, no switch): Unlock PRO,
  // with the trial or the minutes left, at the top left, or Manage plan.
  header = null,
  // The controls' bottom bar, Advanced and Studio only, opening their
  // sheets the way the controls do (./index.jsx `setSheetOpen`): the host
  // holds `openSheet`, and `renderBody(card)` wraps the card in the same
  // sliding area with the sheet under it. Without them, the help links stay.
  openSheet = null,
  onSheetToggle = null,
  renderBody = (card) => card,
  studioUrl = null,
  // The Dark skin: the framed prompt wears it too.
  dark = false,
  onOpenYouTubeClick,
  accessSite,
  spotifyBeta = false,
  appleMusicBeta = false,
  bandcampBeta = false,
  studioIdle,
  siteAccessBusy,
  siteAccessError,
  onEnableSiteClick,
  onUpgradeClick,
  onManageSubscriptionClick,
  onFeedbackClick,
  onHelpClick,
  onChangelogClick,
  onRateAppClick,
}) {
  // Studio's prompt, until it can't load; then the gate's own buttons.
  const [promptUnavailable, setPromptUnavailable] = useState(false);
  const prompt = Boolean(toolUrl) && !promptUnavailable;
  const plan = describePlan({
    isPro,
    trialMode,
    trialEnded,
    remainingTrialDays,
    usageHit,
    usageRemaining,
    usagePeriod,
  });
  const feedbackEnabled = isFeedbackEnabled !== false;
  // Spotify is offered by the builds that carry it (sites/index.js), and
  // only once the listener has switched Spotify (Beta) on under Advanced;
  // Apple Music and Bandcamp once their betas are on.
  const spotify = isSpotifyEnabled() && spotifyBeta;
  const appleMusic = appleMusicBeta === true;
  const bandcamp = bandcampBeta === true;
  const streamingSites = [
    "SoundCloud",
    ...(spotify ? ["Spotify"] : []),
    ...(appleMusic ? ["Apple Music"] : []),
    ...(bandcamp ? ["Bandcamp"] : []),
  ];
  const siteNames = siteList(["YouTube", ...streamingSites]);
  const links = [
    feedbackEnabled && {
      key: "feedback",
      label: "Share feedback",
      onClick: () => onFeedbackClick?.("general"),
    },
    feedbackEnabled && {
      key: "bug",
      label: "Report a bug",
      onClick: () => onFeedbackClick?.("bug"),
    },
    { key: "faq", label: "FAQ", onClick: () => onHelpClick?.() },
    { key: "changelog", label: "What’s new", onClick: () => onChangelogClick?.() },
    isRateAppEnabled !== false && {
      key: "rate",
      label: "Rate Slowed",
      onClick: () => onRateAppClick?.(),
    },
  ].filter(Boolean);

  return (
    <div className="unsupported-gate">
      {header || (
        <div className="unsupported-gate__brand" aria-label="Slowed">
          <Logo isPro={isPro} />
        </div>
      )}

      {renderBody(
      <div className="unsupported-gate__hero">
        {(accessSite || studioIdle || !toolUrl) && (
          <span className="unsupported-gate__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="22" height="22">
              <path d="M8 5.5v13l10.5-6.5z" fill="currentColor" />
            </svg>
          </span>
        )}
        {accessSite ? (
          <SiteAccess
            site={accessSite}
            busy={siteAccessBusy === true}
            error={siteAccessError}
            onEnableSiteClick={onEnableSiteClick}
          />
        ) : studioIdle ? (
          <>
            <h1 className="unsupported-gate__title">
              Play a song in Slowed Studio
            </h1>
            <p className="unsupported-gate__text">
              Drop a song file, or paste a{" "}
              {siteList(["YouTube", ...streamingSites], "or")}{" "}
              link, and play it. Then click Slowed again: these controls work
              on it here too.
            </p>
          </>
        ) : prompt ? (
          <>
            {/* Slowed Studio's prompt, as its own home and slowed.app's
                landing show it: a link (a song) and a few words (a mood),
                and Slow It opens Studio with them. */}
            <h1 className="unsupported-gate__title unsupported-gate__title--prompt">
              Slowed + reverb any song
            </h1>
            <StudioPromptEmbed
              toolUrl={toolUrl}
              dark={dark}
              onSlowIt={(url) => onPromptSlowIt?.(url)}
              onUnavailable={() => setPromptUnavailable(true)}
            />
          </>
        ) : (
          <>
            <h1 className="unsupported-gate__title">
              {toolUrl ? "Slow down any song" : `Slowed works on ${siteNames}`}
            </h1>
            {!toolUrl && (
              <p className="unsupported-gate__text">
                {`Open a video on YouTube, or a song on ${siteList(streamingSites, "or")}, then click Slowed again.`}
              </p>
            )}
            <div className="unsupported-gate__ctas">
              <button
                type="button"
                className="unsupported-gate__cta unsupported-gate__cta--primary"
                onClick={() => onOpenYouTubeClick?.(YOUTUBE_URL)}
              >
                Open YouTube
              </button>
              <button
                type="button"
                className="unsupported-gate__cta"
                onClick={() => onOpenYouTubeClick?.(YOUTUBE_MUSIC_URL)}
              >
                Open YouTube Music
              </button>
              <button
                type="button"
                className="unsupported-gate__cta"
                onClick={() => onOpenYouTubeClick?.(SOUNDCLOUD_URL)}
              >
                Open SoundCloud
              </button>
              {spotify && (
                <button
                  type="button"
                  className="unsupported-gate__cta"
                  onClick={() => onOpenYouTubeClick?.(SPOTIFY_URL)}
                >
                  Open Spotify (Beta)
                </button>
              )}
              {appleMusic && (
                <button
                  type="button"
                  className="unsupported-gate__cta"
                  onClick={() => onOpenYouTubeClick?.(APPLE_MUSIC_URL)}
                >
                  Open Apple Music (Beta)
                </button>
              )}
              {bandcamp && (
                <button
                  type="button"
                  className="unsupported-gate__cta"
                  onClick={() => onOpenYouTubeClick?.(BANDCAMP_URL)}
                >
                  Open Bandcamp (Beta)
                </button>
              )}
            </div>
            {toolUrl && (
              <>
                <span className="unsupported-gate__or" aria-hidden="true">
                  or
                </span>
                {/* Studio plays the listener's own files, with the same
                    sound, and exports them (its Download menu: Audio and
                    Video). The file is chosen in Studio, not here: Chrome
                    closes the popup when the file dialog opens. */}
                <button
                  type="button"
                  className="unsupported-gate__studio unsupported-gate__cta--tool"
                  onClick={() => onAddSongClick?.()}
                >
                  <span className="unsupported-gate__studio-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="18" height="18">
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                  </span>
                  <span className="unsupported-gate__studio-text">
                    <span className="unsupported-gate__studio-head">
                      <span className="unsupported-gate__studio-title">
                        Add Song
                      </span>
                      <span className="unsupported-gate__studio-format">
                        Audio
                      </span>
                      <span className="unsupported-gate__studio-format">
                        Video
                      </span>
                    </span>
                    <span className="unsupported-gate__studio-detail">
                      Choose a song file and play it slowed + reverb in Slowed Studio.
                    </span>
                  </span>
                  <span className="unsupported-gate__studio-arrow" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="16" height="16">
                      <path d="M9 6l6 6-6 6" />
                    </svg>
                  </span>
                </button>
              </>
            )}
          </>
        )}
      </div>,
      )}

      {!header && (
      <div className="unsupported-gate__plan">
        <div className="unsupported-gate__plan-text">
          <span className="unsupported-gate__plan-title">{plan.title}</span>
          <span className="unsupported-gate__plan-detail">{plan.detail}</span>
          {upgradeError && (
            <span className="unsupported-gate__plan-error" role="alert">
              {upgradeError}
            </span>
          )}
        </div>
        {isPro ? (
          // Stripe's billing portal, a subscription's or a lifetime
          // purchase's (Payhook's manage_plan opens both).
          <button
            type="button"
            className="unsupported-gate__plan-button"
            disabled={billingLoading}
            aria-busy={billingLoading}
            onClick={() => onManageSubscriptionClick?.()}
          >
            {billingLoading ? "Loading…" : hideManagePlan ? "Billing" : "Manage plan"}
          </button>
        ) : (
          <button
            type="button"
            className="unsupported-gate__plan-button unsupported-gate__plan-button--unlock"
            onClick={() => onUpgradeClick?.()}
          >
            Unlock PRO
          </button>
        )}
      </div>
      )}

      {onSheetToggle ? (
        <div className="unsupported-gate__footer">
          <ActionsFooter
            activeSheet={openSheet}
            hiddenActions={["mood", "skin", "feedback"]}
            studioUrl={studioUrl}
            onHelpClick={() => onSheetToggle("help")}
            onStudioClick={() => onSheetToggle("studio")}
          />
        </div>
      ) : (
      <nav className="actions unsupported-gate__links" aria-label="Help">
        <div className="actions__list">
          {links.map((link) => (
            <a
              key={link.key}
              href="#!"
              onClick={(event) => {
                event.preventDefault();
                link.onClick();
              }}
            >
              {link.label}
            </a>
          ))}
        </div>
      </nav>
      )}
    </div>
  );
}
